import React, { memo, useState, useCallback, useEffect } from 'react';
import { PRIMARY } from '../utils/parameterUtils';

/**
 * SettingsRow - Contains dimension selector, advanced settings button, and select params
 * Memoized to prevent unnecessary re-renders
 */
const SettingsRow = memo(function SettingsRow({
  dimensionConfig,
  settings,
  onSettingsChange,
  selectParams,
  textInputs,
  onTextInputChange,
  additionalParamsCount,
  onAdvancedSettingsOpen,
  formatParamLabel,
}) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Reset focused index when dropdown closes
  useEffect(() => {
    if (!openDropdown) {
      setFocusedIndex(-1);
    }
  }, [openDropdown]);

  // Handle keyboard navigation in dropdown
  const handleDropdownKeyDown = useCallback((e, options, onSelect) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => Math.min(prev + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          onSelect(options[focusedIndex]);
          setOpenDropdown(null);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpenDropdown(null);
        break;
      case 'Tab':
        setOpenDropdown(null);
        break;
      default:
        break;
    }
  }, [focusedIndex]);

  // Parse options for select params
  const parseOptions = useCallback((param) => {
    let options = param.allowed_values;
    if (typeof options === 'string') {
      try { options = JSON.parse(options); } 
      catch { options = options.split(',').map(v => v.trim()); }
    }
    if (!Array.isArray(options)) options = [options];
    return options;
  }, []);

  // Derived dimension values from structured config
  const availableRatios = dimensionConfig?.ratios || [];
  const availableQualities = dimensionConfig?.qualities || [];
  const hasQualitySelector = availableQualities.length > 1 || (availableQualities.length === 1 && availableQualities[0] !== 'default');
  const activeRatio = settings?.ratio || dimensionConfig?.default_ratio || availableRatios[0] || null;
  const activeQuality = settings?.quality || dimensionConfig?.default_quality || availableQualities[0] || null;

  // Build current label
  const currentLabel = (() => {
    const parts = [];
    if (activeRatio) parts.push(activeRatio);
    if (hasQualitySelector && activeQuality && activeQuality !== 'default') parts.push(activeQuality);
    return parts.length > 0 ? parts.join(' · ') : 'Size';
  })();

  return (
    <>
      {/* Main Settings Row */}
      <div style={{ 
        display: 'flex', 
        gap: 12, 
        marginBottom: 16,
        alignItems: 'stretch',
      }}>
        {/* Dimension Selector - Ratio + Quality */}
        {availableRatios.length > 0 && (
          <div style={{ position: 'relative', flex: 1 }}>
            <button
              onClick={() => setOpenDropdown(openDropdown === 'dimension' ? null : 'dimension')}
              aria-haspopup="listbox"
              aria-expanded={openDropdown === 'dimension'}
              aria-label={`Size selector, current: ${currentLabel}`}
              style={{
                width: '100%',
                height: 52,
                padding: '0 16px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                color: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PRIMARY} strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                </svg>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>Size</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{currentLabel}</div>
                </div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            
            {openDropdown === 'dimension' && (
              <>
                <div 
                  style={{ position: 'fixed', inset: 0, zIndex: 100 }} 
                  onClick={() => setOpenDropdown(null)} 
                />
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  marginBottom: 8,
                  background: '#1a1a1a',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 14,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.7)',
                  zIndex: 200,
                  padding: 12,
                }}>
                  {/* Ratio chips */}
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Ratio</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: hasQualitySelector ? 12 : 0 }}>
                    {availableRatios.map(ratio => (
                      <button
                        key={ratio}
                        onClick={() => onSettingsChange({ ratio })}
                        style={{
                          padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                          background: activeRatio === ratio ? PRIMARY : 'rgba(255,255,255,0.08)',
                          color: activeRatio === ratio ? '#fff' : '#ccc',
                        }}
                      >{ratio}</button>
                    ))}
                    {dimensionConfig?.has_auto && (
                      <button
                        onClick={() => onSettingsChange({ ratio: 'auto' })}
                        style={{
                          padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                          background: activeRatio === 'auto' ? PRIMARY : 'rgba(255,255,255,0.08)',
                          color: activeRatio === 'auto' ? '#fff' : '#ccc',
                        }}
                      >Auto</button>
                    )}
                  </div>
                  {/* Quality chips */}
                  {hasQualitySelector && (
                    <>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Quality</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {availableQualities.map(quality => (
                          <button
                            key={quality}
                            onClick={() => onSettingsChange({ quality })}
                            style={{
                              padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                              background: activeQuality === quality ? PRIMARY : 'rgba(255,255,255,0.08)',
                              color: activeQuality === quality ? '#fff' : '#ccc',
                            }}
                          >{quality}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        
        {/* Advanced Settings Button */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={onAdvancedSettingsOpen}
            aria-label={`Advanced settings${additionalParamsCount > 0 ? `, ${additionalParamsCount} parameters configured` : ''}`}
            style={{
              width: 52,
              height: 52,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
          {additionalParamsCount > 0 && (
            <span style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              background: PRIMARY,
              color: '#fff',
              fontSize: 10,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 5px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {additionalParamsCount}
            </span>
          )}
        </div>
      </div>
      
      {/* Select Params Row */}
      {selectParams.length > 0 && (
        <div style={{ 
          display: 'flex', 
          gap: 12, 
          marginBottom: 16,
          flexWrap: 'wrap',
        }}>
          {selectParams.map(param => {
            const options = parseOptions(param);
            const isOptional = Number(param.is_required) !== 1 && param.is_required !== true;
            const rawValue = textInputs[param.parameter_name];
            const currentValue = rawValue !== null && rawValue !== undefined
              ? rawValue
              : (isOptional ? null : (param.default_value || options[0]));
            const displayLabel = formatParamLabel(param.parameter_name, param.parameter_title);
            const isOpen = openDropdown === param.parameter_name;
            
            return (
              <div key={param.parameter_name} style={{ position: 'relative', flex: 1, minWidth: 140 }}>
                <button
                  onClick={() => setOpenDropdown(isOpen ? null : param.parameter_name)}
                  style={{
                    width: '100%',
                    height: 52,
                    padding: '0 16px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    color: '#fff',
                  }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>{displayLabel}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: currentValue === null ? '#6b7280' : undefined, fontStyle: currentValue === null ? 'italic' : undefined }}>
                      {currentValue ?? '—'}
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                
                {isOpen && (
                  <>
                    <div 
                      style={{ position: 'fixed', inset: 0, zIndex: 100 }} 
                      onClick={() => setOpenDropdown(null)} 
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: 8,
                      background: '#1a1a1a',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 14,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.7)',
                      zIndex: 200,
                      overflow: 'hidden',
                      maxHeight: 280,
                      overflowY: 'auto',
                    }}>
                      {isOptional && (
                        <button
                          onClick={() => {
                            onTextInputChange(param.parameter_name, null);
                            setOpenDropdown(null);
                          }}
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            background: currentValue === null ? 'rgba(250, 139, 20, 0.1)' : 'transparent',
                            border: 'none',
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                            color: '#6b7280',
                            fontSize: 14,
                            fontStyle: 'italic',
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                        >
                          — None —
                        </button>
                      )}
                      {options.map(opt => {
                        const isSelected = currentValue !== null && currentValue === opt;
                        return (
                          <button
                            key={opt}
                            onClick={() => {
                              onTextInputChange(param.parameter_name, opt);
                              setOpenDropdown(null);
                            }}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: isSelected ? 'rgba(250, 139, 20, 0.15)' : 'transparent',
                              border: 'none',
                              borderBottom: '1px solid rgba(255,255,255,0.06)',
                              color: isSelected ? PRIMARY : '#e5e5e5',
                              fontSize: 14,
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                            }}
                          >
                            <div style={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: 6,
                              border: isSelected ? `2px solid ${PRIMARY}` : '2px solid rgba(255,255,255,0.2)',
                              background: isSelected ? PRIMARY : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              {isSelected && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </div>
                            <span style={{ fontWeight: isSelected ? 600 : 400 }}>{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
});

export default SettingsRow;
